// getLivestreamChatData.js
'use strict';
import { Collection } from 'immutable';
import { LruMapCache } from 're-reselect';
import createCachedSelector from '../resource/createCachedSelector.js';

const defaultSelectPath = [];
const defaultDataKey = '';

/**
 * Select chat message bodies from the livestreamChat slice. The input
 * selector reads down to the leaf, so unrelated writes do not invalidate
 * the cache.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {array} [selectPath = []] - select path. default is [].
 * @param {string} [dataKey = ''] - data key. default is ''.
 * @return {any} The selected live stream chat data.
 * @example
 * getLivestreamChatData(state, [streamId, chatMessageId], 'text');
 * // => 'mockChatMessageText'
 * @example
 * getLivestreamChatData(state, [streamId], chatMessageId);
 * // => { id: chatMessageId, text: 'mockChatMessageText', senderId: 'mockSenderId' }
 */
const getLivestreamChatData = createCachedSelector(
  (state, selectPath = defaultSelectPath, dataKey = defaultDataKey) =>
    state.getIn(
      dataKey
        ? ['livestreamChat', ...selectPath, dataKey]
        : ['livestreamChat', ...selectPath]
    ),
  data => (data instanceof Collection ? data.toJS() : data)
)({
  keySelector: (
    state,
    selectPath = defaultSelectPath,
    dataKey = defaultDataKey
  ) => `${selectPath.join(':')}:${dataKey}`,
  // Chat keys churn per message, so an LRU bounds the cache. Keep size well
  // above LIVESTREAM_CHAT_LIST_LIMITATION (remote config, now 200) × ~13
  // leaf reads per row, or active entries evict each other (re-render storm).
  cacheObject: new LruMapCache({ cacheSize: 8192 }),
});

export default getLivestreamChatData;
